import React from "react";
import { ChevronDown, ExternalLink, PackageCheck, Truck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const valueOrDash = (value) => value === null || value === undefined || value === "" ? "—" : value;

const label = (value) => String(value || "pending").replaceAll("_", " ");

const safeExternalUrl = (value) => {
  if (!value || typeof value !== "string") {
    return null;
  }

  try {
    const url = new URL(value);
    return ["http:", "https:"].includes(url.protocol) ? url.toString() : null;
  } catch {
    return null;
  }
};

export const OrderFulfillmentDetails = ({ order }) => {
  const shipment = order.shipment_summary;
  const trackingUrl = safeExternalUrl(shipment?.tracking_url);

  if (!shipment || shipment.provider !== "shiprocket") {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-xs font-medium text-[#64748B]"
        data-testid={`admin-shipment-empty-${order.id}`}
      >
        <Truck size={13} aria-hidden="true" />
        Not shipped
      </span>
    );
  }

  const statusLabel = shipment.awb ? "AWB assigned" : "Awaiting AWB";
  const statusTone = shipment.awb
    ? "border-emerald-200 bg-[#EBF2EE] text-[#1E4A38]"
    : "border-amber-200 bg-[#FEF3C7] text-[#92400E]";

  return (
    <span
      className="inline-block font-sans text-xs text-[#1E2A24]"
      data-testid={`admin-shipment-details-${order.id}`}
    >
      <Popover>
        <PopoverTrigger asChild>
          <button
            aria-label={`View Shiprocket details for order ${order.id}`}
            className="flex items-center gap-1.5"
            data-testid={`admin-shipment-details-toggle-${order.id}`}
            type="button"
          >
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 font-semibold ${statusTone}`}
              data-testid={`admin-shipment-state-${order.id}`}
            >
              {shipment.awb ? <PackageCheck size={13} aria-hidden="true" /> : <Truck size={13} aria-hidden="true" />}
              {statusLabel}
            </span>
            {shipment.awb && (
              <code
                className="max-w-24 truncate font-mono text-[11px] font-semibold text-[#1E2A24]"
                data-testid={`admin-shipment-awb-${order.id}`}
                title={shipment.awb}
              >
                {shipment.awb}
              </code>
            )}
            <span
              className={`rounded-full border px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                shipment.mock
                  ? "border-amber-200 bg-[#FEF3C7] text-[#92400E]"
                  : "border-emerald-200 bg-[#EBF2EE] text-[#1E4A38]"
              }`}
              data-testid={`admin-shipment-mode-${order.id}`}
            >
              {shipment.mock ? "Mock" : "Live"}
            </span>
            <ChevronDown className="text-[#64748B]" size={14} aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-80 rounded-none border-[#D9C3A5] bg-white p-4 text-[#1E2A24] shadow-xl"
          data-testid={`admin-shipment-popover-${order.id}`}
          sideOffset={8}
        >
          <div className="mb-3 flex items-center justify-between border-b border-[#E2DCD2] pb-2">
            <span className="font-semibold text-[#1E2A24]">Shiprocket shipment</span>
            <span className="text-[11px] text-[#64748B]">{label(shipment.status)}</span>
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-[11px]">
          <div>
            <dt className="text-[#64748B]">AWB</dt>
            <dd className="break-all font-mono text-[#1E2A24]">
              {valueOrDash(shipment.awb)}
            </dd>
          </div>
          <div>
            <dt className="text-[#64748B]">Courier</dt>
            <dd data-testid={`admin-shipment-courier-${order.id}`}>{valueOrDash(shipment.courier)}</dd>
          </div>
          <div>
            <dt className="text-[#64748B]">Shipment ID</dt>
            <dd className="break-all font-mono" data-testid={`admin-shipment-id-${order.id}`}>
              {valueOrDash(shipment.shipment_id)}
            </dd>
          </div>
          <div>
            <dt className="text-[#64748B]">Shiprocket order ID</dt>
            <dd className="break-all font-mono" data-testid={`admin-shiprocket-order-id-${order.id}`}>
              {valueOrDash(shipment.shiprocket_order_id)}
            </dd>
          </div>
          <div>
            <dt className="text-[#64748B]">Pickup location</dt>
            <dd data-testid={`admin-shipment-pickup-location-${order.id}`}>
              {valueOrDash(shipment.pickup_location)}
            </dd>
          </div>
          <div>
            <dt className="text-[#64748B]">Updated</dt>
            <dd data-testid={`admin-shipment-updated-at-${order.id}`}>
              {shipment.updated_at ? new Date(shipment.updated_at).toLocaleString("en-IN") : "—"}
            </dd>
          </div>
          </dl>
          <span className="sr-only" data-testid={`admin-shipment-tracking-status-${order.id}`}>
            {label(shipment.status)}
          </span>
          {trackingUrl && (
            <a
              className="mt-4 inline-flex items-center gap-1 font-semibold text-[#2D5A47] underline"
              data-testid={`admin-shipment-tracking-link-${order.id}`}
              href={trackingUrl}
              rel="noreferrer"
              target="_blank"
            >
              Open carrier tracking
              <ExternalLink size={13} aria-hidden="true" />
            </a>
          )}
        </PopoverContent>
      </Popover>
    </span>
  );
};